// Customer-facing language for the pipeline. load-generator-ui's tracker
// says "stage: shipping · failed"; a storefront customer should see
// "Delivery — there's a problem". Same data (the read model's `failedStage`
// / per-stage `reason`), reframed (#187).

import type { OrderStageName } from "./orderStatus";

/** The four pipeline stages, as order milestones a customer would recognise. */
export const MILESTONES: Record<OrderStageName, { label: string; doneLabel: string }> = {
  payment: { label: "Payment", doneLabel: "Payment confirmed" },
  inventory: { label: "Items", doneLabel: "Items reserved" },
  shipping: { label: "Delivery", doneLabel: "Handed to the carrier" },
  fraud: { label: "Security check", doneLabel: "Security check passed" },
};

// The read model's failure `reason` strings (un-prefixed data-driven ones
// from ADR 0023, plus the injected SIMULATED_* family) → what a customer
// would be told. An unknown reason falls back to a generic message for that
// milestone.
const REASON_COPY: Record<string, string> = {
  OUT_OF_STOCK: "One or more items sold out before we could reserve them.",
  CARD_DECLINED_LIMIT: "Your card was declined — it's over its limit for this order.",
  CARRIER_UNAVAILABLE: "No carrier covers your delivery area right now.",
  VELOCITY_EXCEEDED: "This order was held for review — a lot of orders came from your account in a short window.",
  RISK_DECLINED: "This order didn't pass our automated fraud checks.",
};

const SIMULATED_COPY = "Our systems hit a snag processing this step. Nothing was charged.";

export function failureMessage(stage: OrderStageName | undefined, reason: string | undefined): string {
  if (reason && REASON_COPY[reason]) return REASON_COPY[reason];
  if (reason && reason.startsWith("SIMULATED_")) return SIMULATED_COPY;
  const where = stage ? MILESTONES[stage].label.toLowerCase() : "one of the steps";
  return `Something went wrong at the ${where} step.`;
}
